import React, { useEffect } from 'react';
import { Box, Typography, FormControl, Select, MenuItem, SelectChangeEvent, FormHelperText } from '@mui/material';
import { getCitiesForState } from '../../../../Constants/Arrays';

interface CityDropdownProps {
  label: string;
  value: string;
  selectedState: string; // State code like 'MP', 'MH'
  error?: string;
  required?: boolean;
  onChange: (name: string, value: string) => void;
  onBlur: (name: string) => void;
}

const CityDropdown: React.FC<CityDropdownProps> = ({
  label,
  value,
  selectedState,
  error,
  required = false,
  onChange,
  onBlur,
}) => {

  useEffect(()=>{
   onChange('city',value);
  },[value, selectedState]);
  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    onChange('city', selectedValue);
  };

  const handleBlur = () => {
    onBlur('city');
  };

  // Get cities for selected state code via shared util
  const availableCities = selectedState ? getCitiesForState(selectedState) : [];
  const sortedCities = availableCities.map(c => c.value).sort((a, b) => a.localeCompare(b));

  return (
    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
      <Typography
        variant="subtitle1"
        sx={{
          minWidth: { xs: '120px', sm: '200px' },
          flexShrink: 0,
          fontSize: '14px',
          fontWeight: 600,
          color: '#212529',
        }}
      >
        {label}
        {required && (
          <Typography component="span" sx={{ color: '#dc3545', ml: 0.5 }}>
            *
          </Typography>
        )}
      </Typography>

      <Box sx={{ flex: 1 }}>
        <FormControl fullWidth error={!!error} disabled={!selectedState}>
          <Select
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            displayEmpty
            sx={{
              height: '48px',
              borderRadius: '8px',
              backgroundColor: '#fff',
              fontSize: '16px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? '#dc3545' : '#dee2e6',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? '#dc3545' : '#007bff',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? '#dc3545' : '#007bff',
                borderWidth: '2px',
              },
              '& .MuiSelect-select': {
                padding: '12px 16px',
                fontSize: '16px',
              },
              '&.Mui-disabled': {
                backgroundColor: '#f8f9fa',
                color: '#6c757d',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 320, // 8 items * ~40px height = 320px
                  '& .MuiMenuItem-root': {
                    fontSize: '16px',
                    padding: '8px 16px',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#e7f1ff',
                      '&:hover': {
                        backgroundColor: '#d1ecf1',
                      },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              <Typography sx={{ color: '#6c757d', fontSize: '16px' }}>
                {selectedState ? 'Select your city' : 'Please select state first'}
              </Typography>
            </MenuItem>
            {sortedCities.slice(0, 8).map((city) => (
              <MenuItem
                key={city}
                value={city}
                sx={{
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#e7f1ff',
                    '&:hover': {
                      backgroundColor: '#d1ecf1',
                    },
                  },
                }}
              >
                {city}
              </MenuItem>
            ))}
            {sortedCities.length > 8 && (
              <MenuItem disabled>
                <Typography sx={{ 
                  fontSize: '12px', 
                  color: '#6c757d', 
                  fontStyle: 'italic',
                  textAlign: 'center',
                  width: '100%'
                }}>
                  ... and {sortedCities.length - 8} more cities available
                </Typography>
              </MenuItem>
            )}
          </Select>
          {error && (
            <FormHelperText sx={{ fontSize: '12px', color: '#dc3545', ml: 0, mt: '4px' }}>
              {error}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

export default CityDropdown;